import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { Star, Instagram, Globe } from 'lucide-react';
import { creatorService } from '@/lib/supabase/services/creator';

const schema = z.object({
  businessName: z.string().min(2, 'Business name must be at least 2 characters'),
  bio: z.string().min(50, 'Bio must be at least 50 characters'),
  social_links: z.object({
    instagram: z.string()
      .transform(val => val.replace('@', ''))
      .optional(),
    website: z.string()
      .transform(val => {
        if (!val) return '';
        return val.startsWith('http') ? val : `https://${val}`;
      })
      .optional()
      .refine(val => !val || /^https?:\/\/.+\..+$/.test(val), {
        message: 'Please enter a valid URL'
      })
  }).optional()
});

type ProfileFormData = z.infer<typeof schema>;

export function ProfileEditor() {
  const { user } = useAuth();
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ProfileFormData>({
    resolver: zodResolver(schema)
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (!user?.id) return;
        
        const profile = await creatorService.getProfile(user.id);
        
        reset({
          businessName: profile?.business_name || '',
          bio: user.bio || '',
          social_links: profile?.social_links || {}
        });
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user?.id, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      if (!user?.id) throw new Error('Not authenticated');
      
      setError(null);
      
      await creatorService.updateProfile(user.id, {
        business_name: data.businessName,
        bio: data.bio,
        social_links: data.social_links
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError(error instanceof Error ? error.message : 'Failed to update profile');
    }
  };

  if (loading) {
    return (
      <div className="bg-earth-800/50 p-6 rounded-lg">
        <p className="text-sand-400">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-earth-800/50 p-6 rounded-lg">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-12 w-12 rounded-full bg-sand-400/10 flex items-center justify-center">
            <Star className="h-6 w-6 text-sand-400" />
          </div>
          <div>
            <h2 className="text-xl font-display">Profile Settings</h2>
            <p className="text-sand-400">Manage your creator profile information</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-sand-300 mb-2">
              Business Name
            </label>
            <input
              {...register('businessName')}
              className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400"
              placeholder="Your business or brand name"
            />
            {errors.businessName && (
              <p className="mt-1 text-sm text-red-500">{errors.businessName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-sand-300 mb-2">
              Bio
            </label>
            <textarea
              {...register('bio')}
              rows={4}
              className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400"
              placeholder="Tell us about yourself and your expertise..."
            />
            {errors.bio && (
              <p className="mt-1 text-sm text-red-500">{errors.bio.message}</p>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-sand-200">Social Links</h3>
            
            <div className="relative">
              <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-sand-400" />
              <input
                {...register('social_links.instagram')}
                className="w-full pl-10 pr-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400"
                placeholder="Instagram username (e.g. @username)"
              />
              {errors.social_links?.instagram && (
                <p className="mt-1 text-sm text-red-500">{errors.social_links.instagram.message}</p>
              )}
            </div>

            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-sand-400" />
              <input
                {...register('social_links.website')}
                className="w-full pl-10 pr-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400"
                placeholder="Website URL"
              />
              {errors.social_links?.website && (
                <p className="mt-1 text-sm text-red-500">{errors.social_links.website.message}</p>
              )}
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-sm text-green-500">Profile updated successfully!</p>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}